<template>
    <ais-instant-search
        :search-client="SearchClient"
        :index-name="productsIndex"
        :insights="true"
    >
        <div id="search">
            <div class="search-inputs">
                <div class="container">
                    <div class="input-inline submit">
                        <div class="search-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="#004173" height="1em" viewBox="0 0 512 512"><path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z"></path></svg>
                        </div>
                        <input
                            ref="search"
                            type="search"
                            v-model="query"
                            @keyup.enter="searchProducts(query)"
                            @focus="openDropdown"
                            @click="openDropdown"
                            placeholder="Search products, figure numbers, and keywords"
                        >
                        <button type="button" class="button search" @click="searchProducts(query)">Search</button>
                        <button type="button" class="button search-mobile" @click="searchProducts(query)">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" height="1em" viewBox="0 0 512 512"><path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z"></path></svg>
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="showDropdown" ref="results" class="results">
                <div class="container">
                    <div class="wrapper">
                        <div class="suggestions">
                            <div class="suggestions-label">
                                products
                            </div>
                            <ais-index :index-name="suggestionsIndex">
                                <!-- Need to reduce suggestions to "4" below medium breakpoint -->
                                <ais-configure :query="query || ''" :facetFilters="[]" :hitsPerPage="productQuantity" />
                                <ais-autocomplete>
                                    <template #default="{ currentRefinement, indices, refine }">
                                        <ul>
                                            <li v-if="indices[0].hits.length == 0" class="lvl1">
                                                <button
                                                    class="link"
                                                    v-text="query"
                                                    @click.prevent="searchProducts(query)"
                                                ></button>
                                            </li>
                                            <li v-for="(hit, i) in indices[0].hits" class="lvl1">
                                                <button
                                                    class="link"
                                                    @mouseenter="previewResults(hit.query)"
                                                    @click.prevent="searchProducts(hit.query)"
                                                >{{ hit.query }}</button>
                                                <ul v-if="i === 0">
                                                    <li v-for="({ category, attr, value }) in suggestedCategories(hit)" class="lvl2">
                                                        <button class="link -subcat"
                                                            @mouseenter="previewResults(hit.query, [`${attr}:${value}`])"
                                                            @click.prevent="searchProducts(hit.query, value)"
                                                        > in {{ category }}
                                                        </button>
                                                    </li>
                                                </ul>

                                                <ul v-if="i === 0">
                                                    <li v-for="({ category, attr, value }) in suggestedCategories(hit)" class="lvl2">
                                                        <button class="link -subcat"
                                                            @mouseenter="previewResults(hit.query, [`${attr}:${value}`])"
                                                            @click.prevent="searchProducts(hit.query, value)"
                                                        > in {{ category }}
                                                        </button>
                                                    </li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </template>
                                </ais-autocomplete>
                            </ais-index>
                        </div>
                        <div class="product-results">
                            <!-- Need to reduce hitsPerPage to "4" below medium breakpoint -->
                            <ais-configure :query="suggestedQuery || ''" :facetFilters="suggestedFacetFilters" :hitsPerPage="productQuantity" />
                            <ais-state-results>
                                <template #default="{ results: { hits, query } }">
                                    <ais-hits v-if="hits.length">
                                        <template #item="{ item }">
                                            <div class="result">
                                                <div v-if="item.detailImage" >
                                                    <a :href="item.uri" tabindex="-1">
                                                        <img :src="item.detailImage" :alt="item.title">
                                                    </a>
                                                </div>
                                                <div>
                                                    <h4><a :href="item.uri" v-text="item.title"></a></h4>
                                                </div>
                                            </div>
                                        </template>
                                    </ais-hits>
                                    <div v-else>
                                        No product results for {{ query }}.
                                    </div>
                                </template>
                            </ais-state-results>
                        </div>
                        <div class="related-results">
                            <div class="related-label">
                                Related
                            </div>
                            <div>
                                <div class="-related">
                                    <ais-index :index-name="resourcesIndex">
                                        <!-- Need to reduce related to "2" below medium breakpoint -->
                                        <ais-configure :query="query" :facetFilters="[]" :hitsPerPage="resourceQuantity" />
                                        <ais-hits>
                                            <template #item="{ item }">
                                                <div class="result">
                                                    <h4><a :href="item.resourceUrl" v-text="item.title" target="_blank"></a></h4>
                                                    <div class="badge">Resource</div>
                                                </div>
                                            </template>
                                        </ais-hits>
                                    </ais-index>

                                    <button @click="searchResources(query)">View all Resources</button>
                                </div>

                                <div class="-related">
                                    <ais-index :index-name="pagesIndex">
                                        <!-- Need to reduce related to "2" below medium breakpoint -->
                                        <ais-configure :query="query" :facetFilters="[]" :hitsPerPage="resourceQuantity" />
                                        <ais-hits>
                                            <template #item="{ item }">
                                                <div class="result">
                                                    <h4><a :href="item.uri" v-text="item.title" target="_blank"></a></h4>
                                                    <!-- Need category added -->
                                                    <div class="badge">{{ item.type }}</div>
                                                </div>
                                            </template>
                                        </ais-hits>
                                    </ais-index>

                                    <button @click="searchPages(query)">View all Pages</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ais-instant-search>
</template>
<script>
import { SearchClient, SearchRouting } from 'vue/AlgoliaSearch'

export default {
    props: {
        indexPrefix: { required: true, type: String }
    },
    data: instance => ({
        SearchClient,
        query: '',
        suggestedQuery: '',
        suggestedFacetFilters: [],
        showProducts: true,
        showResources: true,
        showPages: true,
        productsIndex: instance.indexPrefix + 'products',
        resourcesIndex: instance.indexPrefix + 'resources',
        pagesIndex: instance.indexPrefix + 'pages',
        suggestionsIndex: instance.indexPrefix + 'products_query_suggestions',
        showDropdown: false,
        mobileResultsWidth: 669
    }),

    watch: {
        query(newVal, oldVal) {
            this.suggestedQuery = newVal
        },
    },

    mounted() {
        const params = new URLSearchParams(location.search)
        this.query = params.get('query')

        document.body.addEventListener('click', e => {
            if (!this.$el.contains(e.target) || e.target == this.$refs.results) {
                this.showDropdown = false
            }
        })
        this.$el.addEventListener('keydown', e => {
            if (e.key == 'Escape') {
                this.showDropdown = false
            }
        }),

        window.addEventListener('resize', this.screenWidth);

        this.screenWidth();
    },

    computed: {
        productQuantity() {
            return window.innerWidth < this.mobileResultsWidth ? 4 : 8;
        },
        resourceQuantity() {
            return window.innerWidth < this.mobileResultsWidth ? 2 : 4;
        }
    },

    methods: {
        openDropdown() {
            this.showDropdown = true
        },
        previewResults(query, filters = {}) {
            this.suggestedQuery = query
            this.suggestedFacetFilters = filters
        },
        searchProducts(query, category = '') {
            this.showDropdown = false
            const params = new URLSearchParams()
            params.set('query', query || '')

            const segments = category.split(/\s*>\s*/)
            for (let i = 0; i < segments.length; i++) {
                if (segments[i]) {
                    params.set(`categoryLvl0[${i}]`, segments[i])
                }
            }
            window.location.href = '/products?' + params
        },
        searchResources(query) {
            window.location.href = `/resources-and-downloads?query=${encodeURIComponent(query)}`;
        },
        searchPages(query) {
            window.location.href = `/pages?query=${encodeURIComponent(query)}`;
        },

        suggestedCategories(hit) {
            const matches = Object.entries(hit[`${this.indexPrefix}products`].facets.exact_matches)
                .map(([attr, matches]) => matches.map(match => ({ attr, value: match.value, category: match.value.replace(/^.*>/, '') })))
            return matches ? Array.concat(matches) : []
        },

        screenWidth() {
            this.screenWidth = window.innerWidth;
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.screenWidth);
    }
}
</script>
